import { cloudStorage } from '@tma.js/sdk-react';

import { storage, StorageKey } from '@/lib/storage';

/**
 * La lingua della TMA: quali lingue sono accese, quale sceglie l'utente, dove si salva.
 * Contratto di fronte: `fronti/tma/lingue.md` nel KB (I-283, accettato L-2647).
 *
 * LE LINGUE ACCESE le dice `VITE_I18N_LINGUE` (lista separata da virgole), per ambiente:
 * staging `it,en,es,el`, produzione `it,en`. Variabile assente o vuota ⇒ `it,en`, cioè la
 * produzione di oggi: è il meccanismo che tiene una lingua nuova fuori dalla prod finché
 * qualcuno non la scrive in `.env.production`. `en` c'è sempre, perché è il ripiego.
 *
 * L'ORDINE con cui si sceglie (detector in `lib/i18n.ts`):
 *   1. la scelta salvata dall'utente nelle Preferenze;
 *   2. `?lang=`;
 *   3. `language_code` di Telegram (initData dell'utente), ridotto alla lingua base;
 *   4. le lingue del browser, ridotte alla lingua base e nel loro ordine;
 *   5. `en`.
 * Telegram e browser si riducono QUI alla lingua base: i18next da solo cerca prima il codice
 * esatto in tutta la lista e poi la lingua base, quindi con `it-IT` da Telegram e un browser
 * `en-US, en` sceglierebbe `en` (rilievo di tma-orc su #541).
 *
 * Si salva SOLO una scelta esplicita (`salvaLingua`): una lingua rilevata non si salva,
 * altrimenti chi cambia la lingua di Telegram resterebbe sulla vecchia per sempre. Il rovescio,
 * dichiarato: scegliere nelle Preferenze la stessa lingua di Telegram la FISSA, e non esiste
 * ancora un «segui Telegram».
 *
 * DOVE si salva: la CloudStorage di Telegram è la FONTE (come `hooks/useCloudPref.ts`), la copia
 * locale serve al primo disegno. Un cambio fatto sul dispositivo A arriva a B alla prossima
 * apertura di B. La CloudStorage si legge solo dopo l'init dell'SDK (`useLinguaDalCloud`, montato
 * da `AppShell`): prima `cloudStorage.isSupported()` è falso (@tma.js/sdk 3.2.0, versione «0.0»).
 */

const PREDEFINITE = ['it', 'en'] as const;

export function lingueAttive(valore: string | undefined): string[] {
  const elenco = (valore ?? '')
    .split(',')
    .map((l) => l.trim().toLowerCase())
    .filter((l) => /^[a-z]{2,3}$/.test(l));
  const scelte = elenco.length > 0 ? elenco : [...PREDEFINITE];
  return scelte.includes('en') ? Array.from(new Set(scelte)) : [...new Set(scelte), 'en'];
}

export const LINGUE = lingueAttive(import.meta.env.VITE_I18N_LINGUE as string | undefined);

/** La lingua base di un tag BCP 47 (`es-419` → `es`, `pt-BR` → `pt`), se è accesa. */
export function linguaSupportata(tag: string | null | undefined, lingue: readonly string[] = LINGUE): string | undefined {
  if (!tag) return undefined;
  const base = tag.trim().toLowerCase().split(/[-_]/)[0];
  return lingue.includes(base) ? base : undefined;
}

/** Il nome della lingua scritto nella lingua stessa («Español», «Ελληνικά»). */
export function nomeNativo(lingua: string): string {
  let nome = lingua;
  try {
    nome = new Intl.DisplayNames([lingua], { type: 'language' }).of(lingua) ?? lingua;
  } catch {
    /* Intl.DisplayNames assente: resta il codice */
  }
  return nome.charAt(0).toLocaleUpperCase(lingua) + nome.slice(1);
}

/** Chiave della CloudStorage di Telegram: solo [A-Za-z0-9_-], i punti sono rifiutati. */
const CHIAVE_CLOUD = 'qlash_lingua';

export function linguaSalvata(): string | undefined {
  return linguaSupportata(storage.get(StorageKey.Lingua));
}

/** Le lingue del browser che sono accese, ridotte alla lingua base, nell'ordine dell'utente. */
export function lingueDelBrowser(lingue: readonly string[] = LINGUE): string[] {
  try {
    const elenco = window.navigator.languages?.length ? window.navigator.languages : [window.navigator.language];
    const basi = elenco.map((tag) => linguaSupportata(tag, lingue)).filter((l): l is string => !!l);
    return Array.from(new Set(basi));
  } catch {
    return [];
  }
}

export function linguaDaTelegram(): string | undefined {
  try {
    const tg = (window as unknown as {
      Telegram?: { WebApp?: { initDataUnsafe?: { user?: { language_code?: string } } } };
    }).Telegram;
    return tg?.WebApp?.initDataUnsafe?.user?.language_code || undefined;
  } catch {
    return undefined;
  }
}

function cloudDisponibile(): boolean {
  try {
    return cloudStorage.isSupported();
  } catch {
    return false;
  }
}

/** Una scelta fatta in questa sessione vince sulla CloudStorage che arriva dopo. */
let sceltaInQuestaSessione = false;

/** La scelta esplicita dell'utente: localStorage subito, CloudStorage di Telegram dopo. */
export function salvaLingua(lingua: string): void {
  sceltaInQuestaSessione = true;
  storage.set(StorageKey.Lingua, lingua);
  if (!cloudDisponibile()) return;
  try {
    void cloudStorage.setItem(CHIAVE_CLOUD, lingua).catch(() => {
      /* la copia locale c'è già */
    });
  } catch {
    /* ambiente senza CloudStorage: basta la copia locale */
  }
}

/**
 * La CloudStorage è la fonte: se dice una lingua accesa diversa dalla copia locale, quella vince
 * (la scelta è stata fatta su un altro dispositivo), si salva in locale e si applica. Una scelta
 * fatta in questa sessione nel frattempo vince su di lei. Va chiamata DOPO l'init dell'SDK.
 */
export function idrataLinguaDalCloud(applica: (lingua: string) => void): void {
  if (!cloudDisponibile()) return;
  try {
    void cloudStorage
      .getItem(CHIAVE_CLOUD)
      .then((valore: string) => {
        const lingua = linguaSupportata(valore);
        if (!lingua || sceltaInQuestaSessione || storage.get(StorageKey.Lingua) === lingua) return;
        storage.set(StorageKey.Lingua, lingua);
        applica(lingua);
      })
      .catch(() => {
        /* senza CloudStorage resta la lingua rilevata */
      });
  } catch {
    /* idem */
  }
}
